/**
 * 软导航 pending：顶栏进度由 start → route commit → settle 驱动。
 *
 * Callers: AppProviders、AppShell、ShellContentTransition
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/** 兜底：避免 start 后永远等不到 settle。 */
export const NAVIGATION_PENDING_SAFETY_MS = 8_000;

type NavigationPendingContextValue = {
  /** 软导航进行中（顶栏进度应显示）。 */
  isSoftNavPending: boolean;
  start: () => void;
  markRouteCommitted: () => void;
  /** 强制收起（测试 / 安全兜底）。 */
  settle: () => void;
};

const NavigationPendingContext =
  createContext<NavigationPendingContextValue | null>(null);

/** 同文档、同 origin，且 pathname 变化的软导航 <a>（search-only 不启 pending）。 */
const isSoftNavPathnameAnchorClick = (event: MouseEvent): boolean => {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return false;
  }

  const target = event.target;
  if (!(target instanceof Element)) return false;
  const anchor = target.closest("a");
  if (!anchor || !(anchor instanceof HTMLAnchorElement)) return false;
  if (anchor.target && anchor.target !== "_self") return false;
  if (anchor.hasAttribute("download")) return false;

  const href = anchor.href;
  if (!href) return false;
  if (href.startsWith("mailto:") || href.startsWith("tel:")) return false;

  try {
    const url = new URL(href);
    if (url.origin !== window.location.origin) return false;
    if (url.pathname === window.location.pathname) return false;
  } catch {
    return false;
  }

  return true;
};

/**
 * 软导航 pending 协调器：start → markRouteCommitted → settle。
 */
export function NavigationPendingProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isSoftNavPending, setIsSoftNavPending] = useState(false);
  const generationRef = useRef(0);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef(false);

  const clearSafetyTimer = useCallback(() => {
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
  }, []);

  const finishSettle = useCallback(() => {
    pendingRef.current = false;
    clearSafetyTimer();
    setIsSoftNavPending(false);
  }, [clearSafetyTimer]);

  const settle = useCallback(() => {
    if (!pendingRef.current) return;
    finishSettle();
  }, [finishSettle]);

  const start = useCallback(() => {
    if (pendingRef.current) return;

    clearSafetyTimer();
    pendingRef.current = true;
    const gen = ++generationRef.current;
    setIsSoftNavPending(true);

    safetyTimerRef.current = setTimeout(() => {
      if (generationRef.current !== gen) return;
      settle();
    }, NAVIGATION_PENDING_SAFETY_MS);
  }, [clearSafetyTimer, settle]);

  const markRouteCommitted = useCallback(() => {
    settle();
  }, [settle]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (isSoftNavPathnameAnchorClick(event)) start();
    };
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
    };
  }, [start]);

  useEffect(
    () => () => {
      clearSafetyTimer();
    },
    [clearSafetyTimer],
  );

  const value = useMemo(
    () => ({
      isSoftNavPending,
      start,
      markRouteCommitted,
      settle,
    }),
    [isSoftNavPending, start, markRouteCommitted, settle],
  );

  return (
    <NavigationPendingContext.Provider value={value}>
      {children}
    </NavigationPendingContext.Provider>
  );
}

/** 读取软导航 pending；须在 NavigationPendingProvider 内。 */
export function useNavigationPending(): NavigationPendingContextValue {
  const ctx = useContext(NavigationPendingContext);
  if (!ctx) {
    throw new Error(
      "useNavigationPending must be used within NavigationPendingProvider",
    );
  }
  return ctx;
}

/**
 * Provider 外（如孤立单测）视为非 soft-nav。
 */
export function useOptionalNavigationPending(): Pick<
  NavigationPendingContextValue,
  "isSoftNavPending"
> {
  const ctx = useContext(NavigationPendingContext);
  return {
    isSoftNavPending: ctx?.isSoftNavPending ?? false,
  };
}
